import {
  Box,
  Button,
  Grid,
  Theme,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  EnforcementSchedule,
  EntityPanelActionHandler,
  JobType,
  Rule,
  Violation,
} from 'types';
import React, {
  ChangeEvent,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { getHandler, RuleContext } from './RulesPanel';
import { useFetcher, useLocation, useOutletContext } from 'react-router';
import { useBackButton } from 'hooks/useBackButton';
import { useTranslation } from 'react-i18next';
import FormFrame from 'components/layout/FormFrame';
import { ScrollableFormContainer } from 'components/layout/ScrollableContainer';
import { ValidatorForm } from 'react-material-ui-form-validator';
import DetailsHeader from 'components/layout/DetailsHeader';
import { AppContext } from 'context/appContext';
import AuturaTextField from 'components/inputs/AuturaTextField';
import AuturaDateTimePicker from 'components/inputs/AuturaDateTimePicker';
import SettingsSection from 'components/layout/SettingsSection';
import AuturaDropdown from 'components/inputs/AuturaDropdown';
import TowRuleForm from './TowRuleForm';
import PatrolRuleForm from './PatrolRuleForm';
import TagRuleForm from './TagRuleForm';

export interface RuleTypeFormProps {
  rule: Rule;
  setRule: (rule: Rule) => void;
  violations: Violation[];
  showEndDate: boolean;
  setShowEndDate: (show: boolean) => void;
  schedule: EnforcementSchedule;
  setSchedule: (schedule: EnforcementSchedule) => void;
  handleAutoCompleteChange: (v: Violation[]) => void;
  isMobile: boolean;
}

export const RuleForm = (): ReactNode => {
  const { t } = useTranslation();
  const {
    rule,
    setRule,
    violations,
    onBack,
    formMode,
    displayMode,
    fetcherKey,
    rules,
  } = useOutletContext<RuleContext>();
  const { state } = useContext(AppContext);
  const fetcher = useFetcher({ key: fetcherKey });
  const isMobile = useMediaQuery(useTheme<Theme>().breakpoints.down('lg'));
  const { pathname } = useLocation();

  const canEditRule = state.entityPermissions.rule.update;

  const [showEndDate, setShowEndDate] = useState<boolean>(!!rule.endDate);
  const [schedule, setSchedule] = useState<EnforcementSchedule>(
    rule.data.enforcementSchedule,
  );

  // handle the browser back button
  useBackButton(onBack);

  useEffect(() => {
    setRule({ ...rule, data: { ...rule.data, enforcementSchedule: schedule } });
  }, [schedule]);

  const handleAutoCompleteChange = (v: Violation[]) => {
    const allViolations = v.find((i) => i.name === 'All Violations');
    if (allViolations) {
      setRule({
        ...rule,
        data: { ...rule.data, violations: [allViolations] },
      });
    } else {
      setRule({
        ...rule,
        data: { ...rule.data, violations: v },
      });
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    switch (name) {
      case 'name':
        setRule({ ...rule, name: value });
        break;
      case 'description':
        setRule({ ...rule, description: value });
        break;
    }
  };

  const submitButtonText = useMemo(() => {
    switch (formMode) {
      case 'edit':
        return t('buttons.update', {
          name: t('rule.label', { ns: 'entities' }),
        });
      case 'create':
        return t('buttons.create', {
          name: t('rule.label', { ns: 'entities' }),
        });
      default:
        return '';
    }
  }, [formMode]);

  const handleSubmit = () => {
    if (formMode === 'edit') {
      const newRule: Rule = {
        ...rule,
      };
      fetcher.submit(
        {
          body: JSON.stringify(newRule),
          handler: EntityPanelActionHandler.UPDATE,
        },
        {
          method: 'PUT',
          action: pathname,
          encType: 'application/json',
        },
      );
    } else if (formMode === 'create' && displayMode !== 'tenant') {
      const newRule: Rule = { ...rule };
      if (rule.endDate) {
        newRule.endDate = rule.endDate;
      }
      const newRuleArray: Rule[] = [...rules, newRule];
      fetcher.submit(
        {
          body: JSON.stringify(newRuleArray),
          handler: getHandler({ displayMode, formMode }),
        },
        {
          method: 'PUT',
          encType: 'application/json',
        },
      );
    } else {
      // create tenant rule
      const newRule: Rule = { ...rule };
      if (rule.endDate) {
        newRule.endDate = rule.endDate;
      }

      fetcher.submit(
        {
          body: JSON.stringify(newRule),
          handler: getHandler({ displayMode, formMode }),
        },
        {
          method: 'POST',
          action: pathname,
          encType: 'application/json',
        },
      );
    }
  };

  const childProps: RuleTypeFormProps = {
    rule,
    setRule,
    violations,
    showEndDate,
    setShowEndDate,
    schedule,
    setSchedule,
    handleAutoCompleteChange,
    isMobile,
  };

  return (
    <ValidatorForm onSubmit={handleSubmit}>
      <FormFrame loading={fetcher.state !== 'idle'}>
        <DetailsHeader
          title={rule.name}
          onCancel={onBack}
          submitLabel={submitButtonText}
          readonly={!canEditRule}
          disableSubmit={fetcher.state !== 'idle'}
        />
        <ScrollableFormContainer maxHeight='calc(100dvh - 326px)'>
          <SettingsSection title={t('general')}>
            <Grid container width={'100%'} spacing={1.5}>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  name='name'
                  label={t('rule.name', { ns: 'entities' })}
                  value={rule.name}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaDropdown
                  name='ruleType'
                  required
                  label={t('rule.data.ruleType', { ns: 'entities' })}
                  options={[JobType.TOW, JobType.PATROL, JobType.TAG]}
                  value={rule.data.ruleType}
                  setValue={(e: ChangeEvent<HTMLInputElement>) => {
                    const value = e.target.value as JobType;
                    setRule({
                      ...rule,
                      data: { ...rule.data, ruleType: value },
                    });
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 6,
                }}
              >
                <AuturaTextField
                  name='description'
                  label={t('rule.description', { ns: 'entities' })}
                  value={rule.description}
                  onChange={handleChange}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                  lg: 3,
                }}
              >
                <AuturaDateTimePicker
                  label={t('rule.startDate', { ns: 'entities' })}
                  dateTime={new Date(rule.startDate)}
                  setDateTime={(d: Date | null) =>
                    setRule({ ...rule, startDate: d || '' })
                  }
                  readonly={false}
                  required={true}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                  lg: 3,
                }}
              >
                {showEndDate ? (
                  <AuturaDateTimePicker
                    label={t('rule.endDate', { ns: 'entities' })}
                    dateTime={new Date(rule?.endDate || '')}
                    setDateTime={(d: Date | null) =>
                      setRule({ ...rule, endDate: d || '' })
                    }
                    readonly={false}
                    required={true}
                  />
                ) : (
                  <Box sx={{ height: '100%', py: { xs: 1, lg: 0 } }}>
                    <Button
                      variant='contained'
                      fullWidth
                      color={'secondary'}
                      onClick={() => setShowEndDate(true)}
                      size='medium'
                      sx={{ height: '100%' }}
                    >
                      {t('buttons.end_date', { ns: 'rules' })}
                    </Button>
                  </Box>
                )}
              </Grid>
            </Grid>
          </SettingsSection>
          {rule.data.ruleType === JobType.TOW && (
            <TowRuleForm {...childProps} />
          )}
          {rule.data.ruleType === JobType.PATROL && (
            <PatrolRuleForm {...childProps} />
          )}
          {rule.data.ruleType === JobType.TAG && (
            <TagRuleForm {...childProps} />
          )}
        </ScrollableFormContainer>
      </FormFrame>
    </ValidatorForm>
  );
};
